import React, { useRef } from 'react'
import { handlePrintData } from '../../../utils/PrintData'
import CardHeading from '../../../shared/CardHeading'
import {
  emrDiagnosisHeaders,
  emrItemsHeaders,
  emrPatientPracDetailsList,
  fomrateEMRItemsDataPrescription,
  formatDiagnosisData,
  itemTypeOptions,
  VitalSignsRecordList,
  vitalSignsValues
} from '../../../constants/EmrContants'
import { PrintIcon } from '../../../utils/TableActionIcons'
import { useTranslation } from 'react-i18next'
import { renderIfNotEmpty } from '../../../utils/RenderIfNotEmpty'
import ReportHeader from '../../../shared/ReportHeader'
import ReportFooter from '../../../shared/ReportFooter'
import ChaiReportHeader from '../../../shared/ChaiReportHeader'
import ChaiReportFooter from '../../../shared/ChaiReportFooter'
import usePrescriptionSettings from '../../../hooks/usePrescriptionSettings'
import useMedicalReportSettings from '../../../hooks/useMedicalReportSettings'

function Prescription ({ emrData }) {
  const printRef = useRef(null)
  const { t, i18n } = useTranslation()
  const lang = i18n?.language
  const { prescriptionSettings } = usePrescriptionSettings()
  const { isChaiFormat, businessInfo } = useMedicalReportSettings()

  const groupItemsByCategory = (items = []) => {
    if (!Array.isArray(items) || !items.length) return []
    const groups = items
      .filter(item => item?.selected_category === 'medication-codes')
      .reduce((acc, item) => {
        const category = item?.selected_category || 'Others'
        if (!acc[category]) {
          acc[category] = {
            category,
            label:
              itemTypeOptions?.find(opt => opt.value === category)?.label ||
              category,
            items: []
          }
        }
        acc[category].items.push(item)
        return acc
      }, {})
    return Object.values(groups)
  }

  const medicationItemGroups = groupItemsByCategory(emrData?.items)

  return (
    <div className=''>
      <div
        className={`icon-bg-container absolute top-[21px] ${
          lang == 'ar' ? 'left-[70px]' : 'right-[70px]'
        }`}
        onClick={() =>
          handlePrintData(printRef.current?.innerHTML, 'Prescription')
        }
      >
        <PrintIcon iconSize='20' />
      </div>

      <div className='print-container' ref={printRef}>
        <div className='flex flex-col gap-0.5 min-h-[100vh]' style={{ fontSize: '12px' }}>
          <div>
            {isChaiFormat ? (
              <ChaiReportHeader
                reportName={t('Prescription')}
                businessInfo={businessInfo}
              />
            ) : (
              <ReportHeader reportName={t('Prescription')} />
            )}

            <InfoTable
              title={t('Patient Information')}
              rows={emrPatientPracDetailsList(emrData, t)}
            />

            {prescriptionSettings.vital_signs && (
              <div className='border border-gray-300 rounded mt-2 shadow-sm'>
                <div className='bg-gray-100 p-1 rounded-t'>
                  <CardHeading
                    textClassName='!text-[14px]'
                    title={t('vital_signs')}
                  />
                </div>
                <div className='grid grid-cols-3 gap-y-1 py-1 w-full capitalize'>
                  {VitalSignsRecordList(
                    vitalSignsValues(emrData?.vital_signs),
                    t
                  )?.map((data, index) => (
                    <RowText title={data?.label} value={data?.text} key={index} />
                  ))}
                </div>
              </div>
            )}

            {prescriptionSettings.diagnosis && (
              <div className='border border-gray-300 rounded mt-2 shadow-sm'>
                <div className='bg-gray-100 p-1 rounded-t'>
                  <CardHeading
                    textClassName='!text-[14px]'
                    title={t('Diagnosis')}
                  />
                </div>
                <EmrReportTable
                  headers={emrDiagnosisHeaders}
                  values={formatDiagnosisData(emrData?.diagnosisArr)}
                />
              </div>
            )}

            {prescriptionSettings.items && renderIfNotEmpty(
              medicationItemGroups,
              <div className='border border-gray-300 rounded mt-2 shadow-sm'>
                <div className='bg-gray-100 p-1 rounded-t'>
                  <CardHeading textClassName='!text-[14px]' title={t('Items')} />
                </div>
                {medicationItemGroups?.map(group => (
                  <div key={group?.category}>
                    <div className='flex items-center bg-gray-200 px-2 py-0.5 border-y border-gray-300'>
                      <span className='!font-semibold' style={{ fontSize: '12px' }}>
                        {t(group?.label)}
                      </span>
                    </div>
                    <EmrReportTable
                      headers={emrItemsHeaders}
                      values={fomrateEMRItemsDataPrescription(group?.items)}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className='flex gap-4 w-full justify-end mt-4 print:mt-8'>
              <div className='text-center'>
                <p className='text-[11px] mt-0.5'>{t('Practitioner Stamp')}</p>
                <img
                  src={emrData?.practitioner?.stamp}
                  alt={t('stamp')}
                  className='w-[5rem] h-[5rem] border-gray-200'
                />
              </div>
              <div className='text-center'>
                <p className='text-[11px] mt-0.5'>{t('Practitioner Signature')}</p>
                <img
                  src={emrData?.practitioner?.signature}
                  alt={t('signature')}
                  className='w-[5rem] h-[5rem] border-gray-200'
                />
              </div>
            </div>
          </div>

          <div className={`mt-auto ${isChaiFormat ? 'emr-print-footer' : ''}`}>
            {isChaiFormat ? (
              <>
                <div className='border-t-2 border-gray-200 mb-1' />
                <ChaiReportFooter
                  businessInfo={businessInfo}
                  qrValue={`MRN:${emrData?.patient?.file_no || emrData?.patient?.file_number || emrData?.patient_mrn || ''}|RX:${emrData?.emr_id || emrData?.id || ''}`}
                />
              </>
            ) : (
              <ReportFooter />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const RowText = ({ title, value }) => {
  const { t } = useTranslation()
  return (
    <div className='flex items-center px-1'>
      <p className='!font-semibold me-1' style={{ fontSize: '12px' }}>
        {t(title)}:
      </p>
      <p className='!font-normal' style={{ fontSize: '12px' }}>
        {value}
      </p>
    </div>
  )
}

export const EmrReportTable = ({ headers, values }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  return (
    <table
      className='min-w-full border-collapse'
      style={{ fontSize: '12px' }}
    >
      <thead>
        <tr>
          {headers?.map((header, idx) => (
            <th
              key={idx}
              className={`capitalize border px-1 py-0.5 text-darkBg font-semibold ${
                lang == 'ar' ? 'text-right' : 'text-left'
              }`}
            >
              {t(header)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {values?.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {headers?.map((header, colIndex) => {
              const cellValue = row?.[header]
              return (
                <td key={colIndex} className='border px-1 py-0.5 !font-normal'>
                  {row?.type === 'img' && header === 'value' ? (
                    <img
                      className='h-[60px]'
                      src={`data:image/png;base64,${cellValue}`}
                      alt={t('attachment')}
                    />
                  ) : (
                    cellValue ?? '--'
                  )}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export const InfoTable = ({ title, rows }) => {
  return (
    <div className='border border-gray-300 rounded mb-2'>
      <div className='bg-gray-100 rounded p-0.5'>
        <span className='!font-semibold' style={{ fontSize: '14px' }}>
          {title}
        </span>
      </div>
      <table className='w-full' style={{ fontSize: '12px' }}>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`border p-0.5 ${
                    cellIndex === 0 || cellIndex === 2
                      ? 'font-semibold w-1/4 border-l-0'
                      : ''
                  } ${cellIndex === 3 ? 'border-r-0' : ''}`}
                >
                  <span
                    className={`${
                      cell.bold ? '!font-semibold' : '!font-normal'
                    }`}
                  >
                    {cell.content}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default Prescription
